// ScoreCreditoMeter.jsx
import React, { useMemo } from "react";
import { Badge } from "flowbite-react";

/**
 * ScoreCreditoMeter
 * Visualização horizontal segmentada (faixas de risco) com marcador.
 *
 * Props:
 * - score?: number | null   (ex.: 742)
 * - max?: number            (default: 1000)
 * - min?: number            (default: 0)
 * - title?: string          (default: "Score de Risco")
 * - subtitle?: string       (default: "Faixa 0–1000")
 * - className?: string
 * - layout?: "full" | "half"
 */
export default function ScoreCreditoMeter({
  score,
  max = 1000,
  min = 0,
  title = "Score de Risco",
  subtitle = "Faixa 0–1000",
  className = "",
  layout = "full",
}) {
  const isHalf = layout === "half";

  // validação
  const valid = Number.isFinite(Number(score));
  const clamped = valid ? clamp(Number(score), min, max) : null;

  // percent (0..100)
  const pct = useMemo(() => {
    if (!valid) return 0;
    const span = Math.max(1, max - min);
    return Math.round(((clamped - min) / span) * 100);
  }, [valid, clamped, min, max]);

  const tier = valid ? getTierByPercent(pct) : null;

  // Larguras das bandas por percentual do range (ajuste se quiser outras regras)
  const bands = [
    { key: "crítica", label: "Crítica", from: 0, to: 49, color: "bg-red-500" },
    {
      key: "atenção",
      label: "Atenção",
      from: 50,
      to: 69,
      color: "bg-amber-500",
    },
    { key: "boa", label: "Boa", from: 70, to: 84, color: "bg-blue-500" },
    {
      key: "excelente",
      label: "Excelente",
      from: 85,
      to: 100,
      color: "bg-green-500",
    },
  ];

  function widthPct(from, to) {
    return `${Math.max(0, to - from + 1)}%`; // aproximação visual
  }

  if (!valid) {
    return (
      <div className={`w-full ${className}`}>
        <div className="mt-4 rounded-lg border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mt-4">
            <div className="flex gap-3">
              <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
              <div className="text-sm leading-relaxed text-amber-900">
                Não há informações suficientes para exibir o score.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="mt-4 rounded-lg border border-gray-200 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              {subtitle} • {pct}% do máximo
            </p>
          </div>
          <Badge color={tierBadgeColor(tier)} className="capitalize">
            {tier}
          </Badge>
        </div>

        {/* Meter */}
        <div
          className={`mt-6 ${
            isHalf ? "" : "grid grid-cols-2 gap-8 items-center relative"
          }`}
        >
          <div className={isHalf ? "" : "col-span-1"}>
            <div className="relative w-full">
              {/* Trilho segmentado */}
              <div
                className="w-full h-5 rounded-full overflow-hidden border border-gray-200 bg-gray-100 mt-8"
                aria-label="Faixas do score"
              >
                <div className="flex h-full w-full">
                  {bands.map((b) => (
                    <div
                      key={b.key}
                      className={`${b.color} h-full`}
                      style={{ width: widthPct(b.from, b.to) }}
                      title={`${b.label} (${b.from}%–${b.to}%)`}
                    />
                  ))}
                </div>
              </div>

              {/* Marcador */}
              <div
                className="absolute -top-10"
                style={{ left: `calc(${pct}% - 38px)` }} // centraliza a seta ~20px largura
              >
                <div className="flex flex-col items-center">
                  <div className="px-2 py-0.5 text-[11px] font-semibold rounded bg-white border border-gray-200 shadow-sm">
                    {clamped}/{max}
                  </div>
                  <div
                    className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] mt-1"
                    style={{
                      borderLeftColor: "transparent",
                      borderRightColor: "transparent",
                      borderTopColor: dotColor(tier),
                    }}
                    aria-hidden
                  />
                </div>
              </div>

              {/* Escala (opcional, minimal) */}
              <div className="mt-2 flex justify-between text-[11px] text-gray-500">
                <span>{min}</span>
                <span>{Math.round((min + max) / 2)}</span>
                <span>{max}</span>
              </div>
            </div>
          </div>

          {/* Mensagem contextual */}
          <div className={isHalf ? "mt-4" : "col-span-1"}>
            <ContextMessage90d
              tier={tier}
              score={clamped}
              max={max}
              compact={isHalf}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================= Mensagem 90 dias ======================= */
function ContextMessage90d({ tier, score, max, compact = false }) {
  const pct = Math.round((score / max) * 100);

  const content =
    tier === "excelente" ? (
      <p>
        Probabilidade <strong>muito alta</strong> de pagamento em dia dos
        boletos nos <strong>próximos 90 dias</strong> ({score}/{max} • {pct}%).
      </p>
    ) : tier === "boa" ? (
      <p>
        <strong>Alta probabilidade</strong> de quitação dentro do prazo nos{" "}
        <strong>próximos 90 dias</strong>, com atrasos pontuais possíveis (
        {score}/{max} • {pct}%).
      </p>
    ) : tier === "atenção" ? (
      <p>
        Risco moderado de atrasos nos <strong>próximos 90 dias</strong>.
        Considere limites menores, garantias ou prazos reduzidos ({score}/{max}{" "}
        • {pct}%).
      </p>
    ) : (
      <p>
        <strong>Alta probabilidade</strong> de atraso ou não pagamento nos{" "}
        <strong>próximos 90 dias</strong>. Reavalie condições, peça garantias ou
        pagamento antecipado ({score}/{max} • {pct}%).
      </p>
    );

  return (
    <div
      className={`rounded-2xl border p-4 ${
        compact ? "text-sm" : ""
      } ${messageBorderClass(tier)}`}
    >
      <div className="flex gap-3">
        <span
          className={`mt-2 h-2.5 w-2.5 flex-none rounded-full ${messageDotClass(
            tier
          )}`}
        />
        <div className="leading-relaxed text-gray-700 text-sm">{content}</div>
      </div>
    </div>
  );
}

/* ======================= Helpers ======================= */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Tiers por percentual do máximo */
function getTierByPercent(p) {
  const x = Math.round(p);
  if (x >= 85) return "excelente";
  if (x >= 70) return "boa";
  if (x >= 50) return "atenção";
  return "crítica";
}

function tierBadgeColor(tier) {
  return (
    {
      excelente: "success",
      boa: "info",
      atenção: "warning",
      crítica: "failure",
    }[tier] || "gray"
  );
}

function messageBorderClass(tier) {
  switch (tier) {
    case "excelente":
      return "border-green-200 bg-green-50";
    case "boa":
      return "border-blue-200 bg-blue-50";
    case "atenção":
      return "border-amber-200 bg-amber-50";
    case "crítica":
    default:
      return "border-red-200 bg-red-50";
  }
}

function messageDotClass(tier) {
  switch (tier) {
    case "excelente":
      return "bg-green-500";
    case "boa":
      return "bg-blue-500";
    case "atenção":
      return "bg-amber-500";
    case "crítica":
    default:
      return "bg-red-600";
  }
}

function dotColor(tier) {
  switch (tier) {
    case "excelente":
      return "#22c55e";
    case "boa":
      return "#3b82f6";
    case "atenção":
      return "#f59e0b";
    default:
      return "#dc2626";
  }
}
